import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearch } from "wouter";
import { AccountLayout } from "../components/shell.tsx";
import { Button, Panel, Skeleton } from "../components/ui.tsx";
import { get } from "../lib/api.ts";
import { useSession } from "../lib/session.tsx";
import type { PlansResponse } from "../lib/types.ts";
import { formatInstantBR } from "../../shared/dates.ts";
import { usePageTitle } from "../lib/nav.tsx";

/** T14: minimal identity only (from plan discovery); no plan-content API is called here. */
export function AccessEndedPage({ planId }: { planId: string }) {
  usePageTitle("Acesso encerrado");
  const qc = useQueryClient();
  const { logout } = useSession();
  const removed = new URLSearchParams(useSearch()).get("motivo") === "removido";
  useEffect(() => {
    qc.removeQueries({ queryKey: ["plan", planId] });
  }, [qc, planId]);
  const plans = useQuery({ queryKey: ["plans"], queryFn: () => get<PlansResponse>("/plans?page_size=100") });
  const plan = plans.data?.data.find((p) => p.id === planId);

  return (
    <AccountLayout>
      <Panel className="max-w-xl">
        <h1 className="text-xl font-bold">{removed ? "Acesso removido" : "O acesso a esta oficina foi encerrado"}</h1>
        {plans.isPending ? (
          <Skeleton lines={2} />
        ) : (
          plan && (
            <p className="mt-2 text-sm text-muted">
              {plan.institution.name} · {plan.name}
              {!removed && <> · Acesso dos participantes até {formatInstantBR(plan.access_until)} (horário de Brasília)</>}
            </p>
          )
        )}
        <p className="mt-3">
          {removed
            ? "Seu acesso a esta oficina foi removido pelo instrutor. Se isso não era esperado, entre em contato com ele."
            : "O acesso a esta oficina foi encerrado. Para solicitar uma prorrogação, entre em contato com o instrutor."}
        </p>
        <p className="mt-2 text-sm text-muted">Os dados da oficina foram preservados; nada foi apagado.</p>
        <div className="mt-5 flex flex-wrap gap-2">
          <Link href="/planos" className="inline-flex rounded-ctl bg-primary px-4 py-2 text-sm font-semibold text-white">Voltar para minhas oficinas</Link>
          <Button variant="secondary" onClick={() => void logout()}>Sair</Button>
        </div>
      </Panel>
    </AccountLayout>
  );
}
